import { DatePicker, TimePicker } from "antd";
import { InputComponentProps } from "components/Form/components/InputComponentProps";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localeData from "dayjs/plugin/localeData";
import weekday from "dayjs/plugin/weekday";
import weekOfYear from "dayjs/plugin/weekOfYear";
import weekYear from "dayjs/plugin/weekYear";
import { FormEvent, KeyboardEvent, useCallback, useMemo, useRef } from "react";
import "./GenericDateTime.scss";

export interface DateTimeProps {
    input: InputComponentProps<string>;
    fieldAttr?: any;
    identifier: string;
}

type GenericDateTimeType = "date" | "time" | "datetime-local";

interface GenericDateTimeProps extends DateTimeProps {
    type: GenericDateTimeType;
}

/**
 * Formats a value typed directly into the picker input is allowed to be in,
 * per picker type. These mirror the display formats the picker renders, so a
 * strict parse of the user's inline-typed text round-trips exactly.
 */
const typedInputParseFormats: Record<GenericDateTimeType, string[]> = {
    time: ["HH:mm", "HH:mm:ss"],
    date: ["YYYY-MM-DD"],
    "datetime-local": ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm"],
};

/**
 * Parse text typed inline into a picker input. Returns the dayjs value only for
 * a complete, well-formed value in one of the picker's display formats, and
 * null otherwise (empty, half-typed or garbage text), so incomplete text never
 * replaces a previously valid value.
 */
export const parseTypedText = (
    type: GenericDateTimeType,
    typedText: string | undefined | null
): dayjs.Dayjs | null => {
    dayjs.extend(customParseFormat);
    if (!typedText) {
        return null;
    }
    const parsed = dayjs(typedText, typedInputParseFormats[type], true);
    return parsed.isValid() ? parsed : null;
};

/**
 * True when `candidate` equals the field's current value (string in one of the
 * picker formats, an ISO string, or a dayjs object). Used to avoid committing
 * an unchanged value.
 */
export const isSameAsCurrent = (
    type: GenericDateTimeType,
    candidate: dayjs.Dayjs,
    currentValue: dayjs.ConfigType | undefined | null
): boolean => {
    dayjs.extend(customParseFormat);
    if (!currentValue) {
        return false;
    }
    let current: dayjs.Dayjs | null = null;
    if (dayjs.isDayjs(currentValue)) {
        current = currentValue;
    } else if (typeof currentValue === "string") {
        const strict = dayjs(currentValue, typedInputParseFormats[type], true);
        current = strict.isValid() ? strict : dayjs(currentValue);
    } else {
        current = dayjs(currentValue);
    }
    if (!current?.isValid()) {
        return false;
    }
    const unit = type === "date" ? "day" : "second";
    return candidate.isSame(current, unit);
};

/**
 * The value a date/time picker shows for the field's current value. Memoised by
 * the callers on the raw value: rc-picker re-syncs its staged selection
 * whenever the `value` prop changes identity, so a fresh dayjs object on every
 * render would throw away what the user is typing or picking whenever the
 * surrounding form re-renders.
 */
const toPickerValue = (type: GenericDateTimeType, value: unknown): dayjs.Dayjs | null => {
    if (!value) {
        return null;
    }
    if (type === "time") {
        return dayjs(value as string, ["HH:mm:ss", "HH:mm"]);
    }
    return dayjs(value as dayjs.ConfigType);
};

export const GenericDateTime = ({ identifier, input, fieldAttr, type }: GenericDateTimeProps) => {
    dayjs.extend(customParseFormat);
    dayjs.extend(advancedFormat);
    dayjs.extend(weekday);
    dayjs.extend(localeData);
    dayjs.extend(weekOfYear);
    dayjs.extend(weekYear);

    const rawValue = input.value;
    const pickerValue = useMemo(() => toPickerValue(type, rawValue), [type, rawValue]);

    // antd's date+time and time pickers have an OK button. Without OK, rc-picker
    // discards whatever the user staged — by typing into the input or by
    // clicking in the panel — when the picker closes (outside click, blur,
    // pressing Run). Keep the latest staged value here and commit it when the
    // picker closes, the same way OK does. Escape still cancels.
    const stagedRef = useRef<dayjs.Dayjs | null>(null);
    const cancelledRef = useRef(false);
    const openRef = useRef(false);
    const latest = useRef({ input, rawValue, type });
    latest.current = { input, rawValue, type };

    const commit = useCallback((value: dayjs.Dayjs | null) => {
        stagedRef.current = null;
        latest.current.input.onChange?.(value as any);
    }, []);

    const onOpenChange = useCallback(
        (open: boolean) => {
            fieldAttr?.onOpenChange?.(open);
            openRef.current = open;
            if (open) {
                stagedRef.current = null;
                cancelledRef.current = false;
                return;
            }
            const staged = stagedRef.current;
            stagedRef.current = null;
            if (cancelledRef.current || !staged) {
                return;
            }
            const { type: t, rawValue: current } = latest.current;
            if (!isSameAsCurrent(t, staged, current)) {
                commit(staged);
            }
        },
        [commit, fieldAttr]
    );

    // Panel clicks (a day, an hour, a minute) stage a value without committing.
    const onSelect = useCallback(
        (value: dayjs.Dayjs) => {
            fieldAttr?.onSelect?.(value);
            stagedRef.current = value;
        },
        [fieldAttr]
    );

    // Text typed into the input: stage it once it is a complete, valid value.
    // Half-typed text stages nothing, so it never wipes the previous value.
    const onInput = useCallback((event: FormEvent<HTMLSpanElement>) => {
        const target = event.target as HTMLInputElement;
        const parsed = parseTypedText(latest.current.type, target?.value);
        if (parsed) {
            stagedRef.current = parsed;
        }
    }, []);

    const onKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            fieldAttr?.onKeyDown?.(event);
            if (event.key === "Escape") {
                cancelledRef.current = true;
                // Escape closes the open picker only. Without this the key
                // bubbles to an enclosing dialog (run with options), which
                // closes too and discards everything entered in it.
                if (openRef.current) {
                    event.stopPropagation();
                }
            }
        },
        [fieldAttr]
    );

    const commonProps = useMemo(
        () => ({
            id: identifier,
            ...input,
            ...fieldAttr,
            "aria-label": identifier,
            className: "input__field datePickerInput",
            dropdownClassName: "datePickerPopup",
            allowClear: false,
            onChange: commit,
            onOpenChange,
            onSelect,
            onKeyDown,
        }),
        [fieldAttr, identifier, input, commit, onOpenChange, onSelect, onKeyDown]
    );

    return (
        <span className="genericDateTime" onInput={onInput}>
            {type === "time" ? (
                <TimePicker {...commonProps} format="HH:mm" value={pickerValue} />
            ) : (
                <DatePicker
                    {...commonProps}
                    showTime={type === "datetime-local"}
                    type={type}
                    value={pickerValue}
                />
            )}
        </span>
    );
};
